"use client"

import type React from "react"

import { useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Activity, TrendingUp, Smartphone, Monitor, Chrome, Users, Clock, Download, RefreshCw } from "lucide-react"

// Proper interfaces untuk type safety
interface SystemStats {
  totalRequests?: number
  uniqueVisitors?: number
  avgResponseTime?: number
  successRate?: number
}

interface TrafficAnalyticsContentProps {
  systemStats?: SystemStats
}

interface TrafficDataPoint {
  time: string
  legitimate: number
  bots: number
  total: number
}

interface GeoData {
  country: string
  code: string
  visitors: number
  percentage: number
  color: string
}

interface DeviceData {
  name: string
  value: number
  percentage: number
  color: string
}

interface BrowserData {
  name: string
  value: number
  percentage: number
  color: string
}

interface PerformanceData {
  time: string
  responseTime: number
  throughput: number
  errorRate: number
}

interface TrafficSource {
  source: string
  visitors: number
  percentage: number
  color: string
}

export function TrafficAnalyticsContent({ systemStats = {} }: TrafficAnalyticsContentProps) {
  const [timeRange, setTimeRange] = useState("24h")
  const [selectedMetric, setSelectedMetric] = useState("requests")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Safe number formatting functions
  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toLocaleString()
  }, [])

  const formatPercentage = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0%"
    return `${num.toFixed(1)}%`
  }, [])

  const formatMilliseconds = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0ms"
    return `${num}ms`
  }, [])

  // Safe data generation with error handling
  const generateTrafficData = useCallback((): TrafficDataPoint[] => {
    try {
      return Array.from({ length: 24 }, (_, i) => {
        const legitimate = Math.floor(Math.random() * 100) + 50
        const bots = Math.floor(Math.random() * 50) + 10
        return {
          time: `${i.toString().padStart(2, "0")}:00`,
          legitimate,
          bots,
          total: legitimate + bots,
        }
      })
    } catch (error) {
      console.error("Error generating traffic data:", error)
      return []
    }
  }, [])

  const trafficData = generateTrafficData()

  // Safe static data with proper typing
  const geoData: GeoData[] = [
    { country: "Indonesia", code: "ID", visitors: 2847, percentage: 45.2, color: "#3b82f6" },
    { country: "United States", code: "US", visitors: 1523, percentage: 24.1, color: "#10b981" },
    { country: "Singapore", code: "SG", visitors: 892, percentage: 14.1, color: "#f59e0b" },
    { country: "Malaysia", code: "MY", visitors: 634, percentage: 10.0, color: "#ef4444" },
    { country: "Thailand", code: "TH", visitors: 412, percentage: 6.6, color: "#8b5cf6" },
  ]

  const deviceData: DeviceData[] = [
    { name: "Mobile", value: 3245, percentage: 51.8, color: "#3b82f6" },
    { name: "Desktop", value: 2156, percentage: 34.4, color: "#10b981" },
    { name: "Tablet", value: 867, percentage: 13.8, color: "#f59e0b" },
  ]

  const browserData: BrowserData[] = [
    { name: "Chrome", value: 3456, percentage: 55.2, color: "#3b82f6" },
    { name: "Safari", value: 1234, percentage: 19.7, color: "#10b981" },
    { name: "Firefox", value: 987, percentage: 15.8, color: "#f59e0b" },
    { name: "Edge", value: 567, percentage: 9.1, color: "#ef4444" },
    { name: "Other", value: 24, percentage: 0.4, color: "#8b5cf6" },
  ]

  const performanceData: PerformanceData[] = Array.from({ length: 24 }, (_, i) => ({
    time: `${i.toString().padStart(2, "0")}:00`,
    responseTime: Math.floor(Math.random() * 100) + 80,
    throughput: Math.floor(Math.random() * 1000) + 500,
    errorRate: Math.random() * 2,
  }))

  const trafficSources: TrafficSource[] = [
    { source: "Direct", visitors: 2847, percentage: 45.2, color: "#3b82f6" },
    { source: "Search Engines", visitors: 1523, percentage: 24.1, color: "#10b981" },
    { source: "Social Media", visitors: 892, percentage: 14.1, color: "#f59e0b" },
    { source: "Referrals", visitors: 634, percentage: 10.0, color: "#ef4444" },
    { source: "Email", visitors: 412, percentage: 6.6, color: "#8b5cf6" },
  ]

  // Safe refresh handler
  const handleRefresh = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Simulate data refresh
      await new Promise((resolve) => setTimeout(resolve, 1000))
      // Data would be refreshed here in real implementation
    } catch (error) {
      setError("Failed to refresh data")
      console.error("Refresh error:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Safe export handler
  const handleExport = useCallback(() => {
    try {
      const data = {
        systemStats,
        trafficData,
        geoData,
        deviceData,
        browserData,
        performanceData,
        trafficSources,
        exportDate: new Date().toISOString(),
      }

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `traffic-analytics-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export error:", error)
      setError("Failed to export data")
    }
  }, [systemStats, trafficData, geoData, deviceData, browserData, performanceData, trafficSources])

  // Error boundary component
  const ChartErrorBoundary = ({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) => {
    try {
      return <>{children}</>
    } catch (error) {
      console.error("Chart rendering error:", error)
      return <>{fallback}</>
    }
  }

  const ChartFallback = () => (
    <div className="flex items-center justify-center h-64 text-slate-400">
      <div className="text-center">
        <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <div>Chart data unavailable</div>
      </div>
    </div>
  )

  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Analytics Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-blue-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-6 w-6 text-blue-500" />
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Live</Badge>
            </div>
            <div className="text-2xl font-bold text-blue-400">{formatNumber(systemStats.totalRequests)}</div>
            <div className="text-sm text-slate-400">Total Requests</div>
            <div className="text-xs text-slate-500 mt-1">+12.5% from yesterday</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-green-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-6 w-6 text-green-500" />
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Active</Badge>
            </div>
            <div className="text-2xl font-bold text-green-400">{formatNumber(systemStats.uniqueVisitors)}</div>
            <div className="text-sm text-slate-400">Unique Visitors</div>
            <div className="text-xs text-slate-500 mt-1">+8.3% from yesterday</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-amber-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-6 w-6 text-amber-500" />
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">Avg</Badge>
            </div>
            <div className="text-2xl font-bold text-amber-400">{formatMilliseconds(systemStats.avgResponseTime)}</div>
            <div className="text-sm text-slate-400">Response Time</div>
            <div className="text-xs text-slate-500 mt-1">-5.2% from yesterday</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-purple-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-6 w-6 text-purple-500" />
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">Rate</Badge>
            </div>
            <div className="text-2xl font-bold text-purple-400">{formatPercentage(systemStats.successRate)}</div>
            <div className="text-sm text-slate-400">Success Rate</div>
            <div className="text-xs text-slate-500 mt-1">+0.5% from yesterday</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics Dashboard */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Activity className="mr-2 h-5 w-5 text-cyan-500" />
              Traffic Analytics Dashboard
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="24h">Last 24h</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRefresh} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleExport}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="traffic-flow" className="w-full">
            <TabsList className="bg-slate-800/50 p-1 mb-4">
              <TabsTrigger
                value="traffic-flow"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Traffic Flow
              </TabsTrigger>
              <TabsTrigger
                value="geographic"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Geographic
              </TabsTrigger>
              <TabsTrigger
                value="devices"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Devices & Browsers
              </TabsTrigger>
              <TabsTrigger
                value="performance"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Performance
              </TabsTrigger>
              <TabsTrigger
                value="sources"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                Traffic Sources
              </TabsTrigger>
            </TabsList>

            <TabsContent value="traffic-flow" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Real-time Traffic Flow</h3>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trafficData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="time" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="legitimate"
                          stackId="1"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.6}
                          name="Legitimate Traffic"
                        />
                        <Area
                          type="monotone"
                          dataKey="bots"
                          stackId="1"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.6}
                          name="Bot Traffic"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Traffic Comparison</h3>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trafficData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="time" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="legitimate" stroke="#10b981" strokeWidth={2} name="Legitimate" />
                        <Line type="monotone" dataKey="bots" stroke="#ef4444" strokeWidth={2} name="Bots" />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Total"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="geographic" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Geographic Distribution</h3>
                  <div className="space-y-3">
                    {geoData.map((country, index) => (
                      <div
                        key={`geo-${country.code}-${index}`}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: country.color }}></div>
                          <div>
                            <div className="text-slate-200 font-medium">{country.country}</div>
                            <div className="text-xs text-slate-500">{country.code}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-200 font-bold">{formatNumber(country.visitors)}</div>
                          <div className="text-xs text-slate-500">{formatPercentage(country.percentage)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Visitor Distribution</h3>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={geoData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="visitors"
                          label={({ name, percentage }) => `${name}: ${formatPercentage(percentage)}`}
                        >
                          {geoData.map((entry, index) => (
                            <Cell key={`cell-geo-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="devices" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Device Types</h3>
                  <div className="space-y-3 mb-4">
                    {deviceData.map((device, index) => (
                      <div
                        key={`device-${device.name}-${index}`}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          {device.name === "Mobile" ? (
                            <Smartphone className="h-5 w-5 text-blue-500" />
                          ) : device.name === "Desktop" ? (
                            <Monitor className="h-5 w-5 text-green-500" />
                          ) : (
                            <Monitor className="h-5 w-5 text-amber-500" />
                          )}
                          <span className="text-slate-200">{device.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-200 font-bold">{formatNumber(device.value)}</div>
                          <div className="text-xs text-slate-500">{formatPercentage(device.percentage)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={deviceData} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis type="number" stroke="#9ca3af" />
                        <YAxis dataKey="name" type="category" stroke="#9ca3af" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                        <Bar dataKey="value" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Browser Distribution</h3>
                  <div className="space-y-3 mb-4">
                    {browserData.map((browser, index) => (
                      <div
                        key={`browser-${browser.name}-${index}`}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <Chrome className="h-5 w-5 text-blue-500" />
                          <span className="text-slate-200">{browser.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-200 font-bold">{formatNumber(browser.value)}</div>
                          <div className="text-xs text-slate-500">{formatPercentage(browser.percentage)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={browserData}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ percentage }) => `${formatPercentage(percentage)}`}
                        >
                          {browserData.map((entry, index) => (
                            <Cell key={`cell-browser-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="performance" className="mt-0">
              <div className="grid grid-cols-1 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Performance Metrics</h3>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={performanceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="time" stroke="#9ca3af" />
                        <YAxis yAxisId="left" stroke="#9ca3af" />
                        <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="responseTime"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          name="Response Time (ms)"
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="throughput"
                          stroke="#10b981"
                          strokeWidth={2}
                          name="Throughput (req/s)"
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="errorRate"
                          stroke="#ef4444"
                          strokeWidth={2}
                          name="Error Rate (%)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="sources" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Traffic Sources</h3>
                  <div className="space-y-3">
                    {trafficSources.map((source, index) => (
                      <div
                        key={`source-${source.source}-${index}`}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: source.color }}></div>
                          <span className="text-slate-200">{source.source}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-200 font-bold">{formatNumber(source.visitors)}</div>
                          <div className="text-xs text-slate-500">{formatPercentage(source.percentage)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Source Distribution</h3>
                  <ChartErrorBoundary fallback={<ChartFallback />}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={trafficSources}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="visitors"
                          label={({ source, percentage }) => `${source}: ${formatPercentage(percentage)}`}
                        >
                          {trafficSources.map((entry, index) => (
                            <Cell key={`cell-source-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartErrorBoundary>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  )
}
